import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  KeyRound,
  Plus,
  RefreshCw,
  Save,
  Server,
  Trash2,
} from 'lucide-react';
import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';
import { useAuth } from '@/core/hooks';

type SecretSensitivity = 'public' | 'secret';
type SecretStatus = 'missing' | 'configured' | 'disabled';

interface ApiSecretField {
  envName: string;
  label: string;
  enabled?: boolean;
  sensitivity: SecretSensitivity;
  value: string;
  configured?: boolean;
  notes?: string;
}

interface ApiIntegration {
  id: string;
  name: string;
  enabled?: boolean;
  category: string;
  purpose: string;
  requiredFor: string;
  productionTarget: 'server-env' | 'browser-public' | 'server-database';
  fields: ApiSecretField[];
}

const API_KEY_VAULT_STORAGE_KEY = 'admin:api-key-vault:auto-saved';

const defaultIntegrations: ApiIntegration[] = [
  {
    id: 'openai-admin-ai',
    name: 'OpenAI Admin AI Assistant',
    category: 'AI automation',
    purpose: 'Backend-only AI assistant for administrator questions, operational summaries, alerts and report drafting',
    requiredFor: 'Admin AI Assistant, command bar, briefings and AI report summaries',
    productionTarget: 'server-env',
    fields: [
      { envName: 'OPENAI_API_KEY', label: 'OpenAI API key', enabled: true, sensitivity: 'secret', value: '' },
      { envName: 'OPENAI_MODEL', label: 'OpenAI model', enabled: true, sensitivity: 'public', value: 'gpt-5-mini' },
    ],
  },
  {
    id: 'live-telemetry',
    name: 'Live Carer Telemetry',
    category: 'Carer app',
    purpose: 'Real-time carer status, location and task polling',
    requiredFor: 'Live Carers, check-in, check-out, task control',
    productionTarget: 'server-env',
    fields: [
      {
        envName: 'FUCHSIA_TELEMETRY_API_URL',
        label: 'Telemetry API URL',
        sensitivity: 'public',
        value: 'https://fuchsiagorleston.com/api/live-carers',
      },
    ],
  },
  {
    id: 'email-code-delivery',
    name: 'Email Security Code Delivery',
    category: 'Authentication',
    purpose: 'Server-side email delivery for password resets, reminders and operational notifications. Codes are never exposed in the browser.',
    requiredFor: 'Password reset, scheduled reports, reminders and operational email notifications',
    productionTarget: 'server-database',
    fields: [
      { envName: 'RESEND_API_KEY', label: 'Resend API key', enabled: true, sensitivity: 'secret', value: '' },
      { envName: 'RESEND_FROM_EMAIL', label: 'Resend from email', enabled: true, sensitivity: 'public', value: 'Fuchsia Home Care <security@fuchsiagorleston.com>' },
      { envName: 'SMTP_HOST', label: 'SMTP host, optional alternative', enabled: false, sensitivity: 'public', value: '' },
      { envName: 'SMTP_USERNAME', label: 'SMTP username', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'SMTP_PASSWORD', label: 'SMTP password', enabled: false, sensitivity: 'secret', value: '' },
    ],
  },
  {
    id: 'sms-provider',
    name: 'SMS Provider',
    category: 'Communication',
    purpose: 'Server-side SMS delivery for urgent alerts and future carer/family notifications.',
    requiredFor: 'Missed visit alerts, medication escalation and urgent notifications',
    productionTarget: 'server-database',
    fields: [
      { envName: 'TWILIO_ACCOUNT_SID', label: 'Twilio Account SID', enabled: true, sensitivity: 'secret', value: '' },
      { envName: 'TWILIO_AUTH_TOKEN', label: 'Twilio Auth Token', enabled: true, sensitivity: 'secret', value: '' },
      { envName: 'TWILIO_FROM_NUMBER', label: 'Twilio from number', enabled: true, sensitivity: 'public', value: '' },
    ],
  },
  {
    id: 'server-reminders',
    name: 'Server Reminder Delivery',
    category: 'Automations',
    purpose: 'Backend scheduler delivery for operational reminders when the browser is closed',
    requiredFor: 'Visit reminders, missed medication alerts, unpaid invoice reminders, payroll-ready alerts and retry logging',
    productionTarget: 'server-database',
    fields: [
      { envName: 'REMINDER_ADMIN_EMAIL', label: 'Admin reminder email', enabled: true, sensitivity: 'public', value: 'lowestoft@fuchsiahomecare.co.uk' },
      { envName: 'PUSH_WEBHOOK_URL', label: 'Push webhook URL', enabled: true, sensitivity: 'public', value: '' },
      { envName: 'PUSH_WEBHOOK_TOKEN', label: 'Push webhook token', enabled: true, sensitivity: 'secret', value: '' },
    ],
  },
  {
    id: 'maps-directions',
    name: 'Maps and Directions',
    category: 'Location',
    purpose: 'Directions, mileage estimation, travel-time checks, geofencing support and closest-carer recommendations.',
    requiredFor: 'Carer directions, schedule optimiser, travel time, GPS validation and mileage reports',
    productionTarget: 'server-database',
    fields: [
      { envName: 'GOOGLE_MAPS_API_KEY', label: 'Google Maps API key', enabled: true, sensitivity: 'secret', value: '' },
      { envName: 'GOOGLE_MAPS_REGION', label: 'Maps region', enabled: true, sensitivity: 'public', value: 'GB' },
      { envName: 'MAPBOX_ACCESS_TOKEN', label: 'Mapbox access token, optional alternative', enabled: false, sensitivity: 'secret', value: '' },
    ],
  },
  {
    id: 'accounting-xero',
    name: 'Xero Accounting',
    category: 'Accounting',
    purpose: 'Future export/sync for invoices, payments, contacts and accounting reports.',
    requiredFor: 'Xero invoice export, payment reconciliation and finance reporting',
    productionTarget: 'server-database',
    fields: [
      { envName: 'XERO_CLIENT_ID', label: 'Xero client ID', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'XERO_CLIENT_SECRET', label: 'Xero client secret', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'XERO_TENANT_ID', label: 'Xero tenant ID', enabled: false, sensitivity: 'secret', value: '' },
    ],
  },
  {
    id: 'accounting-quickbooks',
    name: 'QuickBooks Accounting',
    category: 'Accounting',
    purpose: 'Future export/sync for invoices, customers, payments and accounting reports.',
    requiredFor: 'QuickBooks invoice export, payment reconciliation and finance reporting',
    productionTarget: 'server-database',
    fields: [
      { envName: 'QUICKBOOKS_CLIENT_ID', label: 'QuickBooks client ID', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'QUICKBOOKS_CLIENT_SECRET', label: 'QuickBooks client secret', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'QUICKBOOKS_REALM_ID', label: 'QuickBooks realm/company ID', enabled: false, sensitivity: 'secret', value: '' },
    ],
  },
  {
    id: 'calendar-sync',
    name: 'Calendar Sync',
    category: 'Scheduling',
    purpose: 'Future calendar synchronisation for rota, admin tasks, care reviews and reminders.',
    requiredFor: 'Google Calendar, Microsoft Outlook Calendar and branch scheduling sync',
    productionTarget: 'server-database',
    fields: [
      { envName: 'GOOGLE_CALENDAR_CLIENT_ID', label: 'Google Calendar client ID', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'GOOGLE_CALENDAR_CLIENT_SECRET', label: 'Google Calendar client secret', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'MICROSOFT_GRAPH_CLIENT_ID', label: 'Microsoft Graph client ID', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'MICROSOFT_GRAPH_CLIENT_SECRET', label: 'Microsoft Graph client secret', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'MICROSOFT_GRAPH_TENANT_ID', label: 'Microsoft tenant ID', enabled: false, sensitivity: 'secret', value: '' },
    ],
  },
  {
    id: 'nhs-gp-connect',
    name: 'NHS / GP Connect',
    enabled: false,
    category: 'Clinical integration',
    purpose: 'Reserved for NHS/GP Connect integration after official approval, onboarding, DPIA, DSPT and supplier setup.',
    requiredFor: 'GP Connect and NHS record access only after formal approval and implementation setup',
    productionTarget: 'server-env',
    fields: [
      { envName: 'GP_CONNECT_ENABLED', label: 'Official approval complete: true/false', enabled: false, sensitivity: 'public', value: 'false' },
      { envName: 'NHS_CLIENT_ID', label: 'NHS client ID', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'NHS_CLIENT_SECRET', label: 'NHS client secret', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'NHS_ENVIRONMENT', label: 'NHS environment', enabled: false, sensitivity: 'public', value: 'sandbox' },
    ],
  },
  {
    id: 'secure-document-storage',
    name: 'Secure Cloud Document Storage',
    category: 'Documents',
    purpose: 'Encrypted object storage for contracts, HR files, payroll PDFs, signed documents and compliance evidence.',
    requiredFor: 'Cloud document storage, encrypted backups, document versioning and audit packs',
    productionTarget: 'server-database',
    fields: [
      { envName: 'DOCUMENT_STORAGE_PROVIDER', label: 'Provider: local-encrypted, s3, r2 or azure', enabled: true, sensitivity: 'public', value: 'local-encrypted' },
      { envName: 'DOCUMENT_STORAGE_BUCKET', label: 'Storage bucket/container', enabled: false, sensitivity: 'public', value: '' },
      { envName: 'DOCUMENT_STORAGE_REGION', label: 'Storage region', enabled: false, sensitivity: 'public', value: 'eu-west-2' },
      { envName: 'DOCUMENT_STORAGE_ACCESS_KEY_ID', label: 'Storage access key ID', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'DOCUMENT_STORAGE_SECRET_ACCESS_KEY', label: 'Storage secret access key', enabled: false, sensitivity: 'secret', value: '' },
      { envName: 'DOCUMENT_STORAGE_ENDPOINT', label: 'Custom endpoint, optional', enabled: false, sensitivity: 'public', value: '' },
      { envName: 'DOCUMENT_OBJECT_ENCRYPTION', label: 'Object encryption mode', enabled: true, sensitivity: 'public', value: 'server-side-aes-256-gcm' },
    ],
  },
  {
    id: 'sentry',
    name: 'Sentry Error Tracking',
    category: 'Security',
    purpose: 'Frontend error tracking already supported by the application',
    requiredFor: 'Production diagnostics and support',
    productionTarget: 'browser-public',
    fields: [
      { envName: 'VITE_SENTRY_DSN', label: 'Sentry DSN', sensitivity: 'public', value: '' },
    ],
  },
];

function fieldStatus(field: ApiSecretField): SecretStatus {
  if (field.enabled === false) return 'disabled';
  return field.value.trim() || field.configured ? 'configured' : 'missing';
}

function normalizeIntegrations(items: ApiIntegration[]): ApiIntegration[] {
  const defaultFieldNames = new Set(
    defaultIntegrations.flatMap((integration) => integration.fields.map((field) => field.envName))
  );
  const merged = defaultIntegrations.map((defaultIntegration) => {
    const serverIntegration = items.find((item) => item.id === defaultIntegration.id);
    if (!serverIntegration) return { ...defaultIntegration, enabled: defaultIntegration.enabled !== false };

    const fields = defaultIntegration.fields.map((defaultField) => {
      const serverField = serverIntegration.fields.find((field) => field.envName === defaultField.envName);
      return {
        ...defaultField,
        ...serverField,
        enabled: serverField?.enabled ?? defaultField.enabled ?? true,
      };
    });

    const customFields = serverIntegration.fields.filter(
      (field) => !fields.some((item) => item.envName === field.envName) && !defaultFieldNames.has(field.envName)
    );

    return {
      ...defaultIntegration,
      ...serverIntegration,
      enabled: serverIntegration.enabled ?? defaultIntegration.enabled ?? true,
      fields: [...fields, ...customFields.map((field) => ({ ...field, enabled: field.enabled ?? true }))],
    };
  });

  const customIntegrations = items
    .filter((item) => !merged.some((integration) => integration.id === item.id))
    .map((integration) => ({
      ...integration,
      enabled: integration.enabled ?? true,
      fields: integration.fields.map((field) => ({ ...field, enabled: field.enabled ?? true })),
    }));

  return [...merged, ...customIntegrations];
}

function createBlankIntegration(): ApiIntegration {
  return {
    id: `custom-${Date.now()}`,
    name: 'New API integration',
    category: 'Custom',
    purpose: '',
    requiredFor: '',
    productionTarget: 'server-env',
    enabled: true,
    fields: [
      {
        envName: 'NEW_API_KEY',
        label: 'API key',
        enabled: true,
        sensitivity: 'secret',
        value: '',
      },
    ],
  };
}

function getInitialIntegrations(): ApiIntegration[] {
  return normalizeIntegrations(getLocalStorage<ApiIntegration[]>(API_KEY_VAULT_STORAGE_KEY, defaultIntegrations));
}

function formatSavedAt(savedAt: string | null): string {
  if (!savedAt) return 'Not saved yet';

  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(new Date(savedAt));
}

export const ApiKeyVault: React.FC = () => {
  const { user, token } = useAuth();
  const canManageApiKeys = Boolean(user?.roles.some((role) => ['SUPER_ADMIN', 'TECHNICAL_ADMIN'].includes(role)));
  const [integrations, setIntegrations] = useState<ApiIntegration[]>(getInitialIntegrations);
  const [statusIntegrations, setStatusIntegrations] = useState<ApiIntegration[]>(() =>
    normalizeIntegrations(defaultIntegrations.map((integration) => ({
      ...integration,
      fields: integration.fields.map((field) => ({ ...field, value: '', configured: false })),
    })))
  );
  const [visibleSecrets, setVisibleSecrets] = useState<Record<string, boolean>>({});
  const [adminToken, setAdminToken] = useState('');
  const [serverStatus, setServerStatus] = useState<'not-connected' | 'connected' | 'error'>('not-connected');
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(() => {
    const savedItems = getLocalStorage<ApiIntegration[] | null>(API_KEY_VAULT_STORAGE_KEY, null);
    return savedItems ? new Date().toISOString() : null;
  });

  const totals = useMemo(() => {
    const fields = integrations.flatMap((integration) => integration.fields);
    const configured = fields.filter((field) => fieldStatus(field) === 'configured').length;
    const disabled = fields.filter((field) => fieldStatus(field) === 'disabled').length;

    return {
      configured,
      disabled,
      missing: fields.length - configured - disabled,
      total: fields.length,
    };
  }, [integrations]);

  const readonlyTotals = useMemo(() => {
    const fields = statusIntegrations.flatMap((integration) => integration.fields);
    const configured = fields.filter((field) => fieldStatus(field) === 'configured').length;
    const disabled = fields.filter((field) => fieldStatus(field) === 'disabled').length;

    return {
      configured,
      disabled,
      missing: fields.length - configured - disabled,
      total: fields.length,
    };
  }, [statusIntegrations]);

  useEffect(() => {
    if (canManageApiKeys || !token) return;

    const loadReadonlyStatus = async () => {
      try {
        const response = await fetch('/api/admin-config/api-key-status', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        const payload = (await response.json()) as { data?: ApiIntegration[] };
        setStatusIntegrations(normalizeIntegrations(Array.isArray(payload.data) ? payload.data : []));
      } catch {
        toast.error('Could not load API key activation status');
      }
    };

    void loadReadonlyStatus();
  }, [canManageApiKeys, token]);

  const persistIntegrations = (nextIntegrations: ApiIntegration[], showToast = false) => {
    const normalizedIntegrations = normalizeIntegrations(nextIntegrations);
    setIntegrations(normalizedIntegrations);
    const saved = setLocalStorage(API_KEY_VAULT_STORAGE_KEY, normalizedIntegrations);

    if (saved) {
      setLastSavedAt(new Date().toISOString());
      if (showToast) {
        toast.success('API keys verified and auto-saved in this browser');
      }
    } else if (showToast) {
      toast.error('The browser could not save the API key configuration');
    }
  };

  const updateIntegration = (integrationId: string, patch: Partial<ApiIntegration>) => {
    persistIntegrations(
      integrations.map((integration) =>
        integration.id === integrationId ? { ...integration, ...patch } : integration
      )
    );
  };

  const updateField = (
    integrationId: string,
    fieldIndex: number,
    patch: Partial<ApiSecretField>
  ) => {
    persistIntegrations(
      integrations.map((integration) => {
        if (integration.id !== integrationId) return integration;

        return {
          ...integration,
          fields: integration.fields.map((field, index) =>
            index === fieldIndex ? { ...field, ...patch } : field
          ),
        };
      })
    );
  };

  const addField = (integrationId: string) => {
    persistIntegrations(
      integrations.map((integration) => {
        if (integration.id !== integrationId) return integration;

        return {
          ...integration,
          fields: [
            ...integration.fields,
            {
              envName: 'NEW_SECRET',
              label: 'New secret',
              enabled: true,
              sensitivity: 'secret',
              value: '',
            },
          ],
        };
      })
    );
  };

  const removeField = (integrationId: string, fieldIndex: number) => {
    persistIntegrations(
      integrations.map((integration) => {
        if (integration.id !== integrationId) return integration;
        if (integration.fields.length === 1) return integration;

        return {
          ...integration,
          fields: integration.fields.filter((_, index) => index !== fieldIndex),
        };
      })
    );
  };

  const loadFromServer = async () => {
    if (!adminToken.trim() && !token) {
      toast.error('Sign in with the system owner account or enter the server admin token first');
      return;
    }

    setIsSyncing(true);
    try {
      const response = await fetch('/api/admin-config/api-keys', {
        headers: {
          'X-Admin-Config-Token': adminToken.trim(),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }

      const payload = (await response.json()) as { data?: ApiIntegration[] };
      const serverIntegrations = Array.isArray(payload.data) && payload.data.length > 0
        ? normalizeIntegrations(payload.data)
        : normalizeIntegrations(defaultIntegrations);

      persistIntegrations(serverIntegrations);
      setServerStatus('connected');
      toast.success('Server API key configuration loaded');
    } catch {
      setServerStatus('error');
      toast.error('Could not load server configuration');
    } finally {
      setIsSyncing(false);
    }
  };

  const syncIntegrationsToServer = async (
    nextIntegrations: ApiIntegration[],
    successMessage = 'API keys saved securely on the server'
  ) => {
    const response = await fetch('/api/admin-config/api-keys', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Config-Token': adminToken.trim(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ data: nextIntegrations }),
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const payload = (await response.json()) as { data?: ApiIntegration[] };
    if (Array.isArray(payload.data)) {
      persistIntegrations(payload.data);
    } else {
      persistIntegrations(nextIntegrations);
    }

    setServerStatus('connected');
    toast.success(successMessage);
  };

  const saveAll = async () => {
    if (!adminToken.trim() && !token) {
      persistIntegrations(integrations, true);
      toast('Saved locally in this browser. Add the server token when you want to sync to the VPS.');
      return;
    }

    setIsSyncing(true);
    try {
      await syncIntegrationsToServer(integrations);
    } catch {
      setServerStatus('error');
      persistIntegrations(integrations, true);
      toast.error('Server sync failed. The keys were saved locally in this browser.');
    } finally {
      setIsSyncing(false);
    }
  };

  const verifyAndSaveIntegration = async (integrationId: string) => {
    const nextIntegrations = integrations.map((integration) => {
      if (integration.id !== integrationId) return integration;

      return {
        ...integration,
        enabled: integration.enabled ?? true,
        fields: integration.fields.map((field) => {
          const hasValue = Boolean(field.value.trim());
          const alreadyConfigured = Boolean(field.configured);

          return {
            ...field,
            enabled: field.enabled ?? true,
            configured: hasValue || alreadyConfigured,
          };
        }),
      };
    });

    const targetIntegration = nextIntegrations.find((integration) => integration.id === integrationId);
    const hasMissingEnabledKey = targetIntegration?.fields.some(
      (field) => field.enabled !== false && !field.value.trim() && !field.configured
    );

    persistIntegrations(nextIntegrations, true);

    if (hasMissingEnabledKey) {
      toast('Saved. Some enabled keys are still empty, so their functions remain incomplete.');
      return;
    }

    if (!adminToken.trim()) {
      toast.success('Integration verified locally. Add the server token to sync it to the VPS.');
      return;
    }

    setIsSyncing(true);
    try {
      await syncIntegrationsToServer(nextIntegrations, 'Integration verified and saved on the server');
    } catch {
      setServerStatus('error');
      toast.error('Server sync failed. The verified key remains saved locally in this browser.');
    } finally {
      setIsSyncing(false);
    }
  };

  const renderActivationControls = (items: ApiIntegration[], options: { readOnly?: boolean; total?: number } = {}) => {
    const readOnly = Boolean(options.readOnly);
    const totalFields = typeof options.total === 'number'
      ? options.total
      : items.flatMap((integration) => integration.fields).length;

    return (
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
              Activation Controls
            </p>
            <h2 className="mt-1 text-xl font-bold text-slate-950">
              {readOnly ? 'API key activation status' : 'API key ON/OFF controls'}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {readOnly
                ? 'Admin can view which integrations are active, configured, missing or disabled. Only the system owner can change API keys.'
                : 'Turn each API key on or off. Disabled keys stop the related function even if a value is configured.'}
            </p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
            {totalFields} total key fields
          </span>
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-2">
          {items.flatMap((integration) =>
            integration.fields.map((field, fieldIndex) => {
              const fieldEnabled = field.enabled !== false;
              const integrationEnabled = integration.enabled !== false;
              const status = fieldStatus(field);

              return (
                <div
                  key={`${integration.id}-${field.envName}-${fieldIndex}-control`}
                  className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-bold text-slate-950">{field.envName}</p>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          integrationEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'
                        }`}
                      >
                        integration {integrationEnabled ? 'active' : 'inactive'}
                      </span>
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          status === 'configured'
                            ? 'bg-emerald-100 text-emerald-800'
                            : status === 'disabled'
                              ? 'bg-slate-200 text-slate-700'
                              : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {status}
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">{integration.name} - {field.label}</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (!readOnly) updateField(integration.id, fieldIndex, { enabled: !fieldEnabled });
                    }}
                    disabled={readOnly}
                    className={`inline-flex min-w-28 items-center justify-center rounded-md px-4 py-2 text-sm font-bold transition ${
                      fieldEnabled
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-700 text-white'
                    } ${readOnly ? 'cursor-default opacity-90' : fieldEnabled ? 'hover:bg-emerald-700' : 'hover:bg-slate-800'}`}
                    title={readOnly ? 'Only the system owner can activate or deactivate API keys' : undefined}
                  >
                    {fieldEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </section>
    );
  };

  if (!canManageApiKeys) {
    return (
      <div className="space-y-8">
        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-5">
            <div className="rounded-lg bg-[#6c3a80]/10 p-4 text-[#6c3a80]">
              <KeyRound className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Administrator Read-Only View
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">API Key Activation Status</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                You can view which API modules are active or configured. Adding, editing, deleting, saving,
                loading from server and ON/OFF changes are restricted to the system owner or technical admin.
              </p>
            </div>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Configured Keys</p>
            <p className="mt-3 text-3xl font-bold text-slate-950">{readonlyTotals.configured}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Missing Keys</p>
            <p className="mt-3 text-3xl font-bold text-amber-700">{readonlyTotals.missing}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-sm font-medium text-slate-500">Disabled Keys</p>
            <p className="mt-3 text-3xl font-bold text-[#6c3a80]">{readonlyTotals.disabled}</p>
          </div>
        </section>

        {renderActivationControls(statusIntegrations, { readOnly: true, total: readonlyTotals.total })}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-5">
            <div className="rounded-lg bg-[#6c3a80]/10 p-4 text-[#6c3a80]">
              <KeyRound className="h-8 w-8" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                Administrator Configuration
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">API Keys and Integrations</h1>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                Manage integration keys for production services, current modules and future API connections.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => persistIntegrations([...integrations, createBlankIntegration()])}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Plus className="h-4 w-4" />
              Add API
            </button>
            <button
              type="button"
              onClick={loadFromServer}
              disabled={isSyncing}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className="h-4 w-4" />
              Load from server
            </button>
            <button
              type="button"
              onClick={saveAll}
              disabled={isSyncing}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              Save to server
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
          <label className="space-y-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Server admin token
            </span>
            <input
              type="password"
              value={adminToken}
              onChange={(event) => setAdminToken(event.target.value)}
              placeholder="Enter the token stored on the VPS"
              className="h-11 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
            />
          </label>
          <span
            className={`inline-flex h-11 items-center justify-center gap-2 rounded-md px-4 text-sm font-semibold ${
              serverStatus === 'connected'
                ? 'bg-emerald-100 text-emerald-800'
                : serverStatus === 'error'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-slate-100 text-slate-700'
            }`}
          >
            <Server className="h-4 w-4" />
            {serverStatus === 'connected'
              ? 'Server connected'
              : serverStatus === 'error'
                ? 'Server auth failed'
                : 'Server token required'}
          </span>
        </div>
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <span className="font-semibold">Auto-save is active</span>
            <span>Last local save: {formatSavedAt(lastSavedAt)}</span>
          </div>
          <p className="mt-1 leading-6">
            Every key you add, edit, enable or disable is saved automatically in this browser. Use the server token when you want the same configuration saved on the VPS/backend.
          </p>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Configured Keys</p>
          <p className="mt-3 text-3xl font-bold text-slate-950">{totals.configured}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Missing Keys</p>
          <p className="mt-3 text-3xl font-bold text-amber-700">{totals.missing}</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Disabled Keys</p>
          <p className="mt-3 text-3xl font-bold text-[#6c3a80]">{totals.disabled}</p>
        </div>
      </section>

      <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-5 text-sm leading-6 text-emerald-900">
        <div className="flex gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            Production configuration is now saved through the API server. Secret values are not returned to the browser;
            if a secret is already configured, leave its field blank to keep the existing server value.
          </p>
        </div>
      </section>

      {renderActivationControls(integrations, { total: totals.total })}

      <section className="space-y-5">
        {integrations.map((integration) => {
          const configuredCount = integration.fields.filter((field) => fieldStatus(field) === 'configured').length;
          const integrationEnabled = integration.enabled !== false;

          return (
            <article
              key={integration.id}
              className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Integration</span>
                    <input
                      value={integration.name}
                      onChange={(event) => updateIntegration(integration.id, { name: event.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category</span>
                    <input
                      value={integration.category}
                      onChange={(event) => updateIntegration(integration.id, { category: event.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Required for</span>
                    <input
                      value={integration.requiredFor}
                      onChange={(event) => updateIntegration(integration.id, { requiredFor: event.target.value })}
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                    />
                  </label>
                  <label className="space-y-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Target</span>
                    <select
                      value={integration.productionTarget}
                      onChange={(event) =>
                        updateIntegration(integration.id, {
                          productionTarget: event.target.value as ApiIntegration['productionTarget'],
                        })
                      }
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                    >
                      <option value="server-env">Server .env</option>
                      <option value="server-database">Server database</option>
                      <option value="browser-public">Browser public key</option>
                    </select>
                  </label>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => updateIntegration(integration.id, { enabled: !integrationEnabled })}
                    className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                      integrationEnabled
                        ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                        : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                    }`}
                  >
                    {integrationEnabled ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void verifyAndSaveIntegration(integration.id)}
                    disabled={isSyncing}
                    className="rounded-full bg-[#6c3a80] px-3 py-1 text-xs font-semibold text-white transition hover:bg-[#5e3474]"
                  >
                    Verify & save
                  </button>
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                    {configuredCount}/{integration.fields.length} configured
                  </span>
                  <button
                    type="button"
                    onClick={() => persistIntegrations(integrations.filter((item) => item.id !== integration.id))}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-red-200 text-red-700 transition hover:bg-red-50"
                    aria-label={`Delete ${integration.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <label className="mt-4 block space-y-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Purpose</span>
                <input
                  value={integration.purpose}
                  onChange={(event) => updateIntegration(integration.id, { purpose: event.target.value })}
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                />
              </label>

              <div className="mt-5 space-y-3">
                {integration.fields.map((field, index) => {
                  const secretKey = `${integration.id}-${field.envName}-${index}`;
                  const isVisible = visibleSecrets[secretKey] || field.sensitivity === 'public';
                  const status = fieldStatus(field);
                  const fieldEnabled = field.enabled !== false;

                  return (
                    <div
                      key={secretKey}
                      className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 lg:grid-cols-[1fr_1fr_1.3fr_auto_auto]"
                    >
                      <input
                        value={field.envName}
                        onChange={(event) =>
                          updateField(integration.id, index, {
                            envName: event.target.value.toUpperCase().replace(/\s+/g, '_'),
                          })
                        }
                        className="rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                        aria-label="Environment variable name"
                      />
                      <input
                        value={field.label}
                        onChange={(event) => updateField(integration.id, index, { label: event.target.value })}
                        className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                        aria-label="API key label"
                      />
                      <div className="flex rounded-md border border-slate-300 bg-white focus-within:border-[#6c3a80] focus-within:ring-2 focus-within:ring-[#6c3a80]/20">
                        <input
                          type={isVisible ? 'text' : 'password'}
                          value={field.value}
                          onChange={(event) => updateField(integration.id, index, { value: event.target.value })}
                          placeholder={
                            field.sensitivity === 'secret' && field.configured
                              ? 'Configured on server - leave blank to keep'
                              : ''
                          }
                          className="min-w-0 flex-1 rounded-l-md px-3 py-2 text-sm text-slate-900 outline-none"
                          aria-label={`${field.label} value`}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setVisibleSecrets((current) => ({
                              ...current,
                              [secretKey]: !current[secretKey],
                            }))
                          }
                          className="inline-flex w-10 items-center justify-center text-slate-500 hover:text-slate-900"
                          aria-label={isVisible ? 'Hide key' : 'Show key'}
                        >
                          {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <select
                        value={field.sensitivity}
                        onChange={(event) =>
                          updateField(integration.id, index, {
                            sensitivity: event.target.value as SecretSensitivity,
                          })
                        }
                        className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                        aria-label="Sensitivity"
                      >
                        <option value="secret">Secret</option>
                        <option value="public">Public</option>
                      </select>
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => updateField(integration.id, index, { enabled: !fieldEnabled })}
                          className={`rounded-full px-2 py-1 text-xs font-semibold transition ${
                            fieldEnabled
                              ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                          }`}
                        >
                          {fieldEnabled ? 'On' : 'Off'}
                        </button>
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                            status === 'configured'
                              ? 'bg-emerald-100 text-emerald-800'
                              : status === 'disabled'
                                ? 'bg-slate-200 text-slate-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {status === 'configured' && <CheckCircle className="h-3 w-3" />}
                          {status}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeField(integration.id, index)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 text-slate-600 transition hover:bg-white"
                          aria-label={`Remove ${field.label}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => addField(integration.id)}
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <Plus className="h-4 w-4" />
                Add key field
              </button>
            </article>
          );
        })}
      </section>
    </div>
  );
};
